import React, { useMemo, useState } from 'react';
import {
   CRM_PACKAGES,
   CRM_PACKAGE_LOCATIONS,
   CRM_PACKAGE_PROVIDERS,
   CRM_PACKAGE_PLANS,
   CRM_PACKAGE_STATUSES,
} from './crmPackagesMockData';
import './Crm.css';

const fmtAED = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtDate = (value) => {
   if (!value) return '—';
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return '—';
   return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const daysUntil = (value) => {
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return null;
   const today = new Date();
   today.setHours(0, 0, 0, 0);
   d.setHours(0, 0, 0, 0);
   return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const statusPillStyle = (status) => {
   switch (status) {
      case 'Active': return { bg: '#e0f2ea', color: '#1a6641' };
      case 'Expiring Soon': return { bg: '#fef3c7', color: '#92400E' };
      case 'Expired': return { bg: '#f3f4f6', color: '#374151' };
      case 'Cancelled': return { bg: '#fee2e2', color: '#991b1b' };
      default: return { bg: '#f3f4f6', color: '#374151' };
   }
};

function StatusPill({ status }) {
   const cfg = statusPillStyle(status);
   return (
      <span className="ws-pill" style={{ background: cfg.bg, color: cfg.color }}>{status}</span>
   );
}

function UsageBar({ used, total }) {
   const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
   const color = pct >= 100 ? '#1a6641' : pct >= 60 ? '#4a3db8' : '#92400E';
   return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 90 }}>
         <div style={{ fontSize: 10, fontWeight: 700, color: '#111' }}>{used} / {total} washes</div>
         <div style={{ width: '100%', height: 5, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
         </div>
      </div>
   );
}

function DrawerField({ label, value, color }) {
   return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
         <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
         <div style={{ fontSize: 12, color: color || '#111', fontWeight: 600 }}>{value}</div>
      </div>
   );
}

const deliveryPillStyle = (status) => {
   switch (status) {
      case 'Completed': return { bg: '#d1fae5', color: '#065f46' };
      case 'Missed': return { bg: '#fee2e2', color: '#991b1b' };
      case 'Due today': return { bg: '#fef3c7', color: '#92400E' };
      case 'Cancelled': return { bg: '#f3f4f6', color: '#6b7280' };
      default: return { bg: '#eef2ff', color: '#4a3db8' }; // Scheduled
   }
};

function PackageDrawer({ pkg, onClose }) {
   if (!pkg) return null;
   const daysLeft = daysUntil(pkg.expiresOn);
   return (
      <div style={{ width: 320, flexShrink: 0, borderLeft: '1px solid #eee', overflowY: 'auto', background: '#fff' }}>
         <div style={{ background: '#f7f7f9', borderBottom: '1px solid #eee', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 1 }}>
            <div>
               <div style={{ fontSize: 12, fontWeight: 700, color: '#111' }}>Package Detail</div>
               <div style={{ fontSize: 10, color: '#6B5FE4', fontFamily: 'monospace', marginTop: 2 }}>{pkg.id}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
               <StatusPill status={pkg.status} />
               <button onClick={onClose} style={{ fontSize: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#888', lineHeight: 1, padding: '0 4px' }}>✕</button>
            </div>
         </div>

         <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1 / -1' }}>
               <DrawerField label="Customer" value={`${pkg.customer} · ${pkg.email}`} />
            </div>
            <DrawerField label="Vehicle" value={<span>{pkg.vehicle} · <span className="ws-plate">{pkg.plate}</span></span>} />
            <DrawerField label="Car Type" value={pkg.carType} />
            <DrawerField label="Location" value={pkg.location} />
            <DrawerField label="Provider" value={pkg.provider} />
            <DrawerField label="Plan" value={<span style={{ color: '#4a3db8' }}>{pkg.plan}</span>} />
            <DrawerField label="Frequency" value={pkg.frequency} />
            <DrawerField label="Purchased" value={fmtDate(pkg.purchasedOn)} />
            <DrawerField
               label="Expires"
               value={pkg.status === 'Expired' ? fmtDate(pkg.expiresOn) : `${fmtDate(pkg.expiresOn)}${daysLeft != null && daysLeft >= 0 ? ` · ${daysLeft}d left` : ''}`}
               color={pkg.status === 'Expiring Soon' ? '#92400E' : undefined}
            />
            <div style={{ gridColumn: '1 / -1' }}>
               <DrawerField label="Wash Usage" value={<UsageBar used={pkg.washesUsed} total={pkg.planTier} />} />
            </div>
            <DrawerField label="Auto-Renew" value={pkg.autoRenew ? <span style={{ color: '#1a6641' }}>On</span> : <span style={{ color: '#991b1b' }}>Off</span>} />
            {pkg.status === 'Expired' && (
               <DrawerField label="Renewed" value={pkg.renewed ? <span style={{ color: '#1a6641' }}>Yes</span> : <span style={{ color: '#991b1b' }}>No — churned</span>} />
            )}

            <div style={{ gridColumn: '1 / -1' }}>
               <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Financial Breakdown
               </div>
               <div style={{ border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
                  {[
                     { label: 'Package Price', value: fmtAED(pkg.money.packagePrice), bg: '#fff' },
                     { label: 'Platform Fee', value: fmtAED(pkg.money.platformFee), bg: '#f7f5ff', color: '#4a3db8' },
                     { label: 'Charged to Customer', value: fmtAED(pkg.money.customerCharged), bg: '#f9f9f9', bold: true },
                     { label: 'Stripe Processing Fee', value: `−${fmtAED(pkg.money.stripeFee)}`, bg: '#fff', color: '#991b1b' },
                     { label: 'Washta Commission (8%)', value: `+${fmtAED(pkg.money.commission)}`, bg: '#f7f5ff', color: '#4a3db8' },
                     { label: 'Washta Net Revenue', value: fmtAED(pkg.money.washtaNet), bg: '#f0fdf4', color: '#065f46', bold: true },
                     { label: 'Provider Payout', value: fmtAED(pkg.money.providerPayout), bg: '#fff' },
                  ].map((row, i, arr) => (
                     <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: row.bg, borderBottom: i < arr.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                        <span style={{ fontSize: 10, color: '#555' }}>{row.label}</span>
                        <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: row.bold ? 800 : 600, color: row.color || '#111' }}>{row.value}</span>
                     </div>
                  ))}
               </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
               <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Delivery History
               </div>
               <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {pkg.deliveries.map((d, i) => {
                     const cfg = deliveryPillStyle(d.status);
                     return (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10 }}>
                           <span style={{ color: '#555' }}>{d.date}</span>
                           <span style={{ color: '#aaa', flex: 1, textAlign: 'right', marginRight: 8, fontSize: 9 }}>{d.note}</span>
                           <span style={{ padding: '2px 7px', borderRadius: 100, fontWeight: 700, background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>{d.status}</span>
                        </div>
                     );
                  })}
               </div>
            </div>
         </div>
      </div>
   );
}

const CrmPackages = () => {
   const [statusFilter, setStatusFilter] = useState('All Statuses');
   const [planFilter, setPlanFilter] = useState('All Plans');
   const [locationFilter, setLocationFilter] = useState('All Locations');
   const [providerFilter, setProviderFilter] = useState('All Providers');
   const [search, setSearch] = useState('');
   const [selectedPkg, setSelectedPkg] = useState(null);

   const filtered = useMemo(() => {
      const q = search.trim().toLowerCase();
      return CRM_PACKAGES.filter((p) => {
         if (statusFilter !== 'All Statuses' && p.status !== statusFilter) return false;
         if (planFilter !== 'All Plans' && p.plan !== planFilter) return false;
         if (locationFilter !== 'All Locations' && p.location !== locationFilter) return false;
         if (providerFilter !== 'All Providers' && p.provider !== providerFilter) return false;
         if (q && !`${p.customer} ${p.email} ${p.plate}`.toLowerCase().includes(q)) return false;
         return true;
      });
   }, [statusFilter, planFilter, locationFilter, providerFilter, search]);

   const kpis = useMemo(() => {
      const active = CRM_PACKAGES.filter((p) => p.status === 'Active').length;
      const expiringSoon = CRM_PACKAGES.filter((p) => p.status === 'Expiring Soon').length;
      const revenueThisMonth = CRM_PACKAGES
         .filter((p) => p.status === 'Active' || p.status === 'Expiring Soon')
         .reduce((sum, p) => sum + p.money.customerCharged, 0);
      const expiredPkgs = CRM_PACKAGES.filter((p) => p.status === 'Expired');
      const renewedCount = expiredPkgs.filter((p) => p.renewed).length;
      const renewalRate = expiredPkgs.length ? Math.round((renewedCount / expiredPkgs.length) * 100) : 0;
      return { active, expiringSoon, revenueThisMonth, renewalRate };
   }, []);

   const planDistribution = useMemo(() => {
      const counts = {};
      CRM_PACKAGES.forEach((p) => { counts[p.plan] = (counts[p.plan] || 0) + 1; });
      const total = CRM_PACKAGES.length || 1;
      return CRM_PACKAGE_PLANS.map((plan) => ({
         label: plan,
         count: counts[plan] || 0,
         pct: Math.round(((counts[plan] || 0) / total) * 100),
      }));
   }, []);

   const providerSplit = useMemo(() => {
      const counts = {};
      CRM_PACKAGES.forEach((p) => { counts[p.provider] = (counts[p.provider] || 0) + 1; });
      const total = CRM_PACKAGES.length || 1;
      return Object.entries(counts)
         .sort((a, b) => b[1] - a[1])
         .map(([label, count]) => ({ label, count, pct: Math.round((count / total) * 100) }));
   }, []);

   const topLocations = useMemo(() => {
      const counts = {};
      CRM_PACKAGES.forEach((p) => { counts[p.location] = (counts[p.location] || 0) + 1; });
      return Object.entries(counts)
         .sort((a, b) => b[1] - a[1])
         .slice(0, 5)
         .map(([label, count]) => ({ label, count }));
   }, []);

   const notifyExpiring = () => {
      alert(`Notify ${kpis.expiringSoon} customers with packages expiring within 7 days — hook this up to your notifications API.`);
   };

   const exportPackagesCSV = () => {
      const header = ['Package ID', 'Customer', 'Email', 'Plan', 'Vehicle', 'Plate', 'Location', 'Provider', 'Washes Used', 'Washes Total', 'Purchased', 'Expires', 'Amount Paid', 'Status'];
      const lines = filtered.map((p) => [
         p.id, p.customer, p.email, p.plan, p.vehicle, p.plate, p.location, p.provider,
         p.washesUsed, p.planTier, fmtDate(p.purchasedOn), fmtDate(p.expiresOn), p.money.customerCharged, p.status,
      ]);
      const csv = [header, ...lines].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'washta-crm-packages.csv';
      a.click();
      URL.revokeObjectURL(url);
   };

   return (
      <div style={{ display: 'flex', width: '100%', minWidth: 0 }}>
         <div className="ws-view-block" style={{ flex: 1, minWidth: 0 }}>
            <div className="ws-view-title" style={{ marginBottom: 0 }}>Package Subscriptions</div>
            <div className="ws-view-sub">
               Every monthly wash package purchased across all providers, from the customer's point of view. Mock data — wire to the packages API when ready.
            </div>

            <div className="ws-kpi-strip ws-kpi-4">
               <div className="ws-kpi-card ws-hl">
                  <div className="ws-kpi-label">Active Packages</div>
                  <div className="ws-kpi-val ws-purple">{kpis.active}</div>
                  <div className="ws-kpi-delta">Currently in use</div>
               </div>
               <div className="ws-kpi-card ws-gr">
                  <div className="ws-kpi-label">Revenue (Active + Expiring)</div>
                  <div className="ws-kpi-val ws-green">{fmtAED(kpis.revenueThisMonth)}</div>
                  <div className="ws-kpi-delta">Charged to customers</div>
               </div>
               <div className="ws-kpi-card ws-am">
                  <div className="ws-kpi-label">Expiring Soon</div>
                  <div className="ws-kpi-val ws-amber">{kpis.expiringSoon}</div>
                  <div className="ws-kpi-delta">Within 7 days</div>
               </div>
               <div className="ws-kpi-card ws-rd">
                  <div className="ws-kpi-label">Renewal Rate</div>
                  <div className="ws-kpi-val ws-red">{kpis.renewalRate}%</div>
                  <div className="ws-kpi-delta">Of expired packages</div>
               </div>
            </div>

            <div className="ws-filter-row">
               <select className="ws-f-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  {['All Statuses', ...CRM_PACKAGE_STATUSES].map((s) => <option key={s} value={s}>{s}</option>)}
               </select>
               <select className="ws-f-select" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
                  {['All Plans', ...CRM_PACKAGE_PLANS].map((p) => <option key={p} value={p}>{p}</option>)}
               </select>
               <select className="ws-f-select" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                  {['All Locations', ...CRM_PACKAGE_LOCATIONS].map((l) => <option key={l} value={l}>{l}</option>)}
               </select>
               <select className="ws-f-select" value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
                  {['All Providers', ...CRM_PACKAGE_PROVIDERS].map((p) => <option key={p} value={p}>{p}</option>)}
               </select>
               <input
                  className="ws-f-input ws-ml"
                  placeholder="Customer, email, plate…"
                  style={{ width: 170 }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
               />
               <button className="ws-btn-ex" onClick={exportPackagesCSV}>↓ Export</button>
            </div>

            <table className="ws-tbl">
               <thead>
                  <tr>
                     <th>Customer</th>
                     <th>Plan</th>
                     <th>Vehicle</th>
                     <th>Location · Provider</th>
                     <th>Usage</th>
                     <th>Purchased</th>
                     <th>Expires</th>
                     <th className="ws-r">Amount Paid</th>
                     <th>Status</th>
                  </tr>
               </thead>
               <tbody>
                  {filtered.length === 0 ? (
                     <tr>
                        <td colSpan={9} style={{ textAlign: 'center', color: '#aaa', padding: '18px 10px' }}>
                           No packages match the current filters.
                        </td>
                     </tr>
                  ) : filtered.map((p) => {
                     const daysLeft = daysUntil(p.expiresOn);
                     return (
                        <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedPkg(p)}>
                           <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                 <div className="ws-avatar">{p.initials}</div>
                                 <div>
                                    <div style={{ fontWeight: 600, fontSize: 11 }}>{p.customer}</div>
                                    <div style={{ fontSize: 9, color: '#aaa' }}>{p.email}</div>
                                 </div>
                              </div>
                           </td>
                           <td>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#4a3db8' }}>{p.plan}</div>
                              <div style={{ fontSize: 9, color: '#888' }}>{p.frequency}</div>
                           </td>
                           <td>
                              <div style={{ fontSize: 11 }}>{p.vehicle}</div>
                              <div><span className="ws-plate">{p.plate}</span> <span style={{ fontSize: 9, color: '#888' }}>{p.carType}</span></div>
                           </td>
                           <td>
                              <div style={{ fontSize: 11 }}>{p.location}</div>
                              <div style={{ fontSize: 9, color: '#888' }}>{p.provider}</div>
                           </td>
                           <td><UsageBar used={p.washesUsed} total={p.planTier} /></td>
                           <td style={{ fontSize: 11 }}>{fmtDate(p.purchasedOn)}</td>
                           <td style={{ fontSize: 11, color: p.status === 'Expiring Soon' ? '#92400E' : p.status === 'Expired' ? '#991b1b' : '#111' }}>
                              {fmtDate(p.expiresOn)}
                              {p.status !== 'Expired' && p.status !== 'Cancelled' && daysLeft != null && (
                                 <div style={{ fontSize: 9, color: '#aaa' }}>{daysLeft}d left</div>
                              )}
                           </td>
                           <td className="ws-r" style={{ fontWeight: 700 }}>{fmtAED(p.money.customerCharged)}</td>
                           <td><StatusPill status={p.status} /></td>
                        </tr>
                     );
                  })}
               </tbody>
            </table>
            <div style={{ fontSize: 11, color: '#888', marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
               Showing {filtered.length} of {CRM_PACKAGES.length} package subscriptions.
            </div>
         </div>

         {selectedPkg ? (
            <PackageDrawer pkg={selectedPkg} onClose={() => setSelectedPkg(null)} />
         ) : (
            <div className="ws-right-panel">
               <div className="ws-rp-title">Plan Distribution</div>
               {planDistribution.map((d) => (
                  <div className="ws-mix-row" key={d.label}>
                     <span className="ws-mix-label">{d.label.replace(' / mo', '')}</span>
                     <div className="ws-mix-track"><div className="ws-mix-fill" style={{ width: `${d.pct}%` }}></div></div>
                     <span className="ws-mix-val">{d.pct}%</span>
                  </div>
               ))}

               <div className="ws-divider"></div>
               <div className="ws-rp-title">Provider Split</div>
               {providerSplit.map((p) => (
                  <div className="ws-mix-row" key={p.label}>
                     <span className="ws-mix-label">{p.label}</span>
                     <div className="ws-mix-track"><div className="ws-mix-fill" style={{ width: `${p.pct}%`, background: '#92400E' }}></div></div>
                     <span className="ws-mix-val" style={{ color: '#92400E' }}>{p.pct}%</span>
                  </div>
               ))}

               <div className="ws-divider"></div>
               <div className="ws-rp-title">Top Locations</div>
               <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 4 }}>
                  {topLocations.map((l) => (
                     <div key={l.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#555' }}>
                        <span>{l.label}</span>
                        <span style={{ fontWeight: 700, color: '#111' }}>{l.count}</span>
                     </div>
                  ))}
               </div>

               <div className="ws-divider"></div>
               <div className="ws-rp-title">Quick Actions</div>
               <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <button className="ws-btn-sm ws-btn-gh" style={{ textAlign: 'left', fontSize: 10 }} onClick={notifyExpiring}>
                     ⏱ Notify expiring-soon customers
                  </button>
                  <button className="ws-btn-sm ws-btn-gh" style={{ textAlign: 'left', fontSize: 10 }} onClick={exportPackagesCSV}>
                     📤 Export package list CSV
                  </button>
               </div>
            </div>
         )}
      </div>
   );
};

export default CrmPackages;
